import React, { useState, useEffect, useContext, useRef } from 'react';
import './SongEdit.css';
import CoverArtEditor from '../components/CoverArtEditor';
import { AuthContext } from '../AuthContext';
import { DataContext } from '../DataContexts/DataContext';
import { PlaylistDataContext } from '../DataContexts/PlaylistDataContext';
import { UploadContext } from '../DataContexts/UploadContext';
import { API_BASE_URL } from '../config';

// Browse right column (DJB-94/DJB-95): the track editor in the Curator look.
// Edit sits top-right, Delete bottom-right, the black Replace button lives
// next to the player timeline, and the playlist-membership area lets an
// admin add/remove the track to CUSTOM_GLOBAL playlists via removable chips
// plus a search control (persisted straight through updatePlaylist).
// DJB-101: the Tags area works exactly like the playlist one — always-visible
// removable chips plus an add/search control, outside the edit-view, each
// change persisted straight through updateSongMetadata.
const SongEdit = ({ allArtists, selectedArtist, selectedSong }) => {
    const { authData } = useContext(AuthContext) || {};
    const accessToken = authData?.accessToken;
    const { updateSongMetadata, tags, deleteTrack } = useContext(DataContext);
    const { playlists, updatePlaylist } = useContext(PlaylistDataContext);
    const { updateTrack } = useContext(UploadContext);
    const fileInputRef = useRef(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedTitle, setEditedTitle] = useState(selectedSong?.title || '');
    const [editedGenre, setEditedGenre] = useState(selectedSong?.genre || '');
    const [editedSubgenre, setEditedSubgenre] = useState(selectedSong?.subgenre || '');
    const [editedAlbumId, setEditedAlbumId] = useState(selectedSong?.albumId || '');
    const [editedNeverIdle, setEditedNeverIdle] = useState(selectedSong?.neverIdle || false);
    const [editedOndemand, setEditedOndemand] = useState(selectedSong?.ondemand || false);
    const [editedNeverNew, setEditedNeverNew] = useState(selectedSong?.neverNew || false);
    const [editedMix, setEditedMix] = useState(selectedSong?.mix || false);
    const [editedFreeSkipQueue, setEditedFreeSkipQueue] = useState(selectedSong?.freeSkipQueue || false);
    const [editedReleaseYear, setEditedReleaseYear] = useState(selectedSong?.releaseyear || '');
    const [tagSearch, setTagSearch] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [playlistSearch, setPlaylistSearch] = useState('');
    const [showPlaylistDropdown, setShowPlaylistDropdown] = useState(false);
    const [audio, setAudio] = useState(null); // For audio playback
    const [isPlaying, setIsPlaying] = useState(false); // Track play state
    const [currentTime, setCurrentTime] = useState(0); // Current playback time
    const [duration, setDuration] = useState(0); // Total duration of the song
    const [volume, setVolume] = useState(1); // Volume level (0 to 1)
    // DJB-198: all-time request count for the selected track; null while loading
    const [requestCount, setRequestCount] = useState(null);
    const [requestCountError, setRequestCountError] = useState(false);

    useEffect(() => {
        // Sync local state with selectedSong whenever selectedSong changes
        setEditedTitle(selectedSong?.title || '');
        setEditedGenre(selectedSong?.genre || '');
        setEditedSubgenre(selectedSong?.subgenre || '');
        setEditedAlbumId(selectedSong?.albumId || '');
        setEditedNeverIdle(selectedSong?.neverIdle || false);
        setEditedOndemand(selectedSong?.ondemand || false);
        setEditedNeverNew(selectedSong?.neverNew || false);
        setEditedMix(selectedSong?.mix || false);
        setEditedFreeSkipQueue(selectedSong?.freeSkipQueue || false);
        setEditedReleaseYear(selectedSong?.releaseyear || '');
        setPlaylistSearch('');
        setTagSearch('');
        if (selectedSong) {
            // Clean up previous audio instance
            if (audio) {
                audio.pause();
                audio.currentTime = 0;
                setIsPlaying(false);
                setCurrentTime(0);
            }

            // Initialize new audio instance
            const newAudio = new Audio(selectedSong?.s3link || '');
            newAudio.volume = volume; // Set initial volume
            setAudio(newAudio);

            // Event listeners
            const handleMetadataLoad = () => setDuration(newAudio.duration);
            const handleTimeUpdate = () => setCurrentTime(newAudio.currentTime);

            newAudio.addEventListener('loadedmetadata', handleMetadataLoad);
            newAudio.addEventListener('timeupdate', handleTimeUpdate);

            return () => {
                // Cleanup
                newAudio.removeEventListener('loadedmetadata', handleMetadataLoad);
                newAudio.removeEventListener('timeupdate', handleTimeUpdate);
                newAudio.pause();
            };
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSong]);

    useEffect(() => {
        setIsEditing(false); // Exit edit mode when the song changes
    }, [selectedSong]);

    // DJB-198: the request count is deliberately fetched here, per selected
    // track, so the library listing never bulk-loads it for every track
    useEffect(() => {
        setRequestCount(null);
        setRequestCountError(false);
        if (!accessToken || !selectedSong?.id) {
            return undefined;
        }
        let cancelled = false;
        fetch(`${API_BASE_URL}/statistics/tracks/${selectedSong.id}/request-count`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch request count (${response.status})`);
                }
                return response.json();
            })
            .then((body) => {
                if (!cancelled) {
                    setRequestCount(body.requestCount);
                }
            })
            .catch((fetchError) => {
                console.error('Failed to fetch track request count:', fetchError);
                if (!cancelled) {
                    setRequestCountError(true);
                }
            });
        // Ignore responses that land after another track was selected
        return () => {
            cancelled = true;
        };
    }, [accessToken, selectedSong?.id]);

    if (!selectedSong) {
        return <p className="ps-placeholder">No song selected. Please choose a song to view details.</p>;
    }

    // Get all albums of the selected artist
    const artistAlbums = selectedArtist?.albums || [];
    const albumName = artistAlbums.find((album) => album.id === selectedSong.albumId)?.name || 'Unknown Album';

    const allGenres = [...new Set((allArtists || []).flatMap((artist) =>
        (artist.albums || []).flatMap((album) =>
            (album.tracks || []).map((track) => track.genre)
        )
    ))].filter(Boolean);

    const allSubgenres = [...new Set((allArtists || []).flatMap((artist) =>
        (artist.albums || []).flatMap((album) =>
            (album.tracks || []).map((track) => track.subgenre)
        )
    ))].filter(Boolean);

    // Tag membership (DJB-101): mirrors the playlist-membership UI below —
    // always-visible chips plus an add/search control, outside the edit-view
    const songTags = selectedSong.tags || [];
    const availableTags = tags.filter(tag => !songTags.includes(tag.id));

    // Playlist membership (DJB-95): the admin-manageable playlists are the
    // CUSTOM_GLOBAL ones; membership changes persist straight away
    const customGlobalPlaylists = (playlists || []).filter(
        (playlist) => playlist.playlistType === 'CUSTOM_GLOBAL'
    );
    const memberPlaylists = customGlobalPlaylists.filter((playlist) =>
        playlist.tracks.includes(selectedSong.id)
    );
    const availablePlaylists = customGlobalPlaylists.filter(
        (playlist) => !playlist.tracks.includes(selectedSong.id)
    );

    const handleAddToPlaylist = async (playlist) => {
        await updatePlaylist({
            ...playlist,
            tracks: [...playlist.tracks, selectedSong.id],
        });
        setPlaylistSearch('');
    };

    const handleRemoveFromPlaylist = async (playlist) => {
        await updatePlaylist({
            ...playlist,
            tracks: playlist.tracks.filter((trackId) => trackId !== selectedSong.id),
        });
    };

    const persistTags = async (nextTags) => {
        // Same field strip as handleSaveClick: SongManager decorates tracks
        // with artist/album objects the backend does not want back
        const { artist, album, ...songWithoutExtraFields } = selectedSong;
        await updateSongMetadata({ ...songWithoutExtraFields, tags: nextTags });
    };

    const handleAddTag = async (tagId) => {
        await persistTags([...songTags, tagId]);
        setTagSearch('');
    };

    const handleRemoveTag = async (tagId) => {
        await persistTags(songTags.filter(id => id !== tagId));
    };

    const formatDuration = (ms) => {
        const minutes = Math.floor(ms / 60000);
        const seconds = Math.floor((ms % 60000) / 1000).toString().padStart(2, '0');
        return `${minutes}:${seconds}`;
    };

    const formatDate = (dateArray) => {
        const date = new Date(
            dateArray[0],
            dateArray[1] - 1, // Month is zero-indexed
            dateArray[2],
            dateArray[3],
            dateArray[4],
            dateArray[5]
        );
        return date.toLocaleString();
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleInputChange = (setter) => (e) => {
        setter(e.target.value);
    };

    const handleCancelClick = () => {
        setEditedTitle(selectedSong?.title || '');
        setEditedGenre(selectedSong?.genre || '');
        setEditedSubgenre(selectedSong?.subgenre || '');
        setEditedAlbumId(selectedSong?.albumId || '');
        setEditedNeverIdle(selectedSong?.neverIdle || false);
        setEditedOndemand(selectedSong?.ondemand || false);
        setEditedNeverNew(selectedSong?.neverNew || false);
        setEditedMix(selectedSong?.mix || false);
        setEditedFreeSkipQueue(selectedSong?.freeSkipQueue || false);
        setEditedReleaseYear(selectedSong?.releaseyear || '');
        setIsEditing(false);
    };

    const handleSaveClick = async () => {
        // Extract only the song properties, excluding artist and album fields that might be added in SongManager
        const { artist, album, ...songWithoutExtraFields } = selectedSong;
        const updatedSong = {
            ...songWithoutExtraFields,
            title: editedTitle,
            genre: editedGenre,
            subgenre: editedSubgenre,
            albumId: editedAlbumId,
            neverIdle: editedNeverIdle,
            ondemand: editedOndemand,
            neverNew: editedNeverNew,
            mix: editedMix,
            freeSkipQueue: editedFreeSkipQueue,
            releaseyear: editedReleaseYear,
        };

        try {
            await updateSongMetadata(updatedSong);
            setIsEditing(false);
        } catch (error) {
        }
    };

    const handlePlay = () => {
        if (audio) {
            audio.play();
            setIsPlaying(true);
        }
    };

    const handlePause = () => {
        if (audio) {
            audio.pause();
            setIsPlaying(false);
        }
    };

    const handleStop = () => {
        if (audio) {
            audio.pause();
            audio.currentTime = 0; // Reset audio to the start
            setIsPlaying(false);
        }
    };

    const handleTimelineChange = (e) => {
        const newTime = parseFloat(e.target.value);
        if (audio) {
            audio.currentTime = newTime;
        }
        setCurrentTime(newTime);
    };

    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);

        if (audio) {
            audio.volume = newVolume; // Ensure volume is updated immediately
        }
    };

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60).toString().padStart(2, '0');
        return `${minutes}:${seconds}`;
    };

    const handleDeleteClick = async () => {
        if (!selectedSong) return;

        const confirmDelete = window.confirm(`Are you sure you want to delete "${selectedSong.title}"?`);
        if (!confirmDelete) return;

        try {
            await deleteTrack(selectedSong.albumId, selectedSong.id);
        } catch (error) {
        }
    };

    const handleReplaceClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Check if file is MP3 or M4A
        if (!file.name.endsWith('.mp3') && !file.name.endsWith('.m4a')) {
            alert('Only MP3 or M4A files are allowed!');
            return;
        }

        const confirmReplace = window.confirm(
            `Are you sure you want to replace "${selectedSong.title}" with "${file.name}"?`
        );
        if (!confirmReplace) {
            e.target.value = ''; // Reset file input
            return;
        }

        try {
            // Create FormData with the file
            const formData = new FormData();
            formData.append('file', file);

            await updateTrack(selectedSong.id, formData);

            // Reset file input
            e.target.value = '';

        } catch (error) {
            console.error('Failed to replace track:', error);
        }
    };

    const propertyChecks = [
        {
            key: 'neverIdle',
            label: 'Never Idle',
            checked: isEditing ? editedNeverIdle : Boolean(selectedSong.neverIdle),
            onToggle: (isChecked) => {
                setEditedNeverIdle(isChecked);
                // Unchecking one of Mix's prerequisites also unchecks Mix
                if (!isChecked) setEditedMix(false);
            },
        },
        {
            key: 'ondemand',
            label: 'On Demand',
            checked: isEditing ? editedOndemand : Boolean(selectedSong.ondemand),
            onToggle: (isChecked) => {
                setEditedOndemand(isChecked);
                if (!isChecked) setEditedMix(false);
            },
        },
        {
            key: 'neverNew',
            label: 'Never New',
            checked: isEditing ? editedNeverNew : Boolean(selectedSong.neverNew),
            onToggle: (isChecked) => {
                setEditedNeverNew(isChecked);
                if (!isChecked) setEditedMix(false);
            },
        },
        {
            key: 'mix',
            label: 'Mix',
            checked: isEditing ? editedMix : Boolean(selectedSong.mix),
            onToggle: (isChecked) => {
                setEditedMix(isChecked);
                // Mix implies Never New, On Demand and Never Idle
                if (isChecked) {
                    setEditedNeverNew(true);
                    setEditedOndemand(true);
                    setEditedNeverIdle(true);
                }
            },
        },
        {
            key: 'freeSkipQueue',
            label: 'VIP',
            checked: isEditing ? editedFreeSkipQueue : Boolean(selectedSong.freeSkipQueue),
            onToggle: (isChecked) => setEditedFreeSkipQueue(isChecked),
        },
    ];

    return (
        <div className="song-edit">
            {/* Header: title + id left, Edit (or Save/Cancel) top-right */}
            <div className="se-head">
                <div className="se-head-main">
                    {isEditing ? (
                        <input
                            type="text"
                            value={editedTitle}
                            onChange={handleInputChange(setEditedTitle)}
                            className="ps-input se-title-input"
                            aria-label="Track title"
                        />
                    ) : (
                        <h2 className="se-title">{selectedSong.title}</h2>
                    )}
                    <p className="se-id">ID: {selectedSong.id}</p>
                </div>
                <div className="se-head-actions">
                    {isEditing ? (
                        <>
                            <button className="ps-btn ps-btn--primary" onClick={handleSaveClick}>Save</button>
                            <button className="ps-btn ps-btn--ghost" onClick={handleCancelClick}>Cancel</button>
                        </>
                    ) : (
                        <button className="ps-btn ps-btn--primary" onClick={handleEditClick}>Edit</button>
                    )}
                </div>
            </div>

            <div className="se-cover">
                {selectedSong.id ? (
                    <CoverArtEditor resourceId={selectedSong.id} template="track" />
                ) : (
                    <div className="se-cover-placeholder">No Image Available</div>
                )}
            </div>

            {/* Metadata fields */}
            <div className="se-fields">
                <div className="se-field">
                    <span className="se-label">Album</span>
                    <span className="se-value">{albumName}</span>
                </div>
                <div className="se-field">
                    <span className="se-label">Genre</span>
                    {isEditing ? (
                        <input
                            type="text"
                            value={editedGenre}
                            onChange={(e) => setEditedGenre(e.target.value)}
                            className="ps-input"
                            list="genre-options"
                            aria-label="Genre"
                        />
                    ) : (
                        <span className="se-value">{selectedSong.genre || 'N/A'}</span>
                    )}
                    <datalist id="genre-options">
                        {allGenres.map((genre, index) => (
                            <option key={index} value={genre} />
                        ))}
                    </datalist>
                </div>
                <div className="se-field">
                    <span className="se-label">Subgenre</span>
                    {isEditing ? (
                        <input
                            type="text"
                            value={editedSubgenre}
                            onChange={(e) => setEditedSubgenre(e.target.value)}
                            className="ps-input"
                            list="subgenre-options"
                            aria-label="Subgenre"
                        />
                    ) : (
                        <span className="se-value">{selectedSong.subgenre || 'N/A'}</span>
                    )}
                    <datalist id="subgenre-options">
                        {allSubgenres.map((subgenre, index) => (
                            <option key={index} value={subgenre} />
                        ))}
                    </datalist>
                </div>
                <div className="se-field">
                    <span className="se-label">Release Year</span>
                    {isEditing ? (
                        <input
                            type="number"
                            value={editedReleaseYear}
                            onChange={(e) => setEditedReleaseYear(e.target.value)}
                            className="ps-input"
                            aria-label="Release year"
                        />
                    ) : (
                        <span className="se-value">{selectedSong.releaseyear || 'N/A'}</span>
                    )}
                </div>
                <div className="se-field">
                    <span className="se-label">Duration</span>
                    <span className="se-value">{formatDuration(selectedSong.durationMs)}</span>
                </div>
                <div className="se-field">
                    <span className="se-label">Created At</span>
                    <span className="se-value">{formatDate(selectedSong.createdAt)}</span>
                </div>
            </div>

            {/* Tags (DJB-101): chips with × plus an add/search control,
                available without entering the edit-view; changes persist
                immediately — exactly like the playlist membership below */}
            <div className="se-field se-field--stacked">
                <span className="se-label">Tags</span>
                <div className="se-chip-area">
                    {songTags.length === 0 && (
                        <span className="se-muted">No tags assigned</span>
                    )}
                    {songTags.map((tagId) => {
                        const tag = tags.find(t => t.id === tagId);
                        return (
                            <span key={tagId} className="ps-tag-chip browse-chip">
                                {tag ? tag.name : tagId}
                                <button
                                    type="button"
                                    className="browse-chip-remove"
                                    aria-label={`Remove tag ${tag ? tag.name : tagId}`}
                                    onClick={() => handleRemoveTag(tagId)}
                                >
                                    ×
                                </button>
                            </span>
                        );
                    })}
                    <div className="se-add-dropdown">
                        <input
                            type="text"
                            placeholder="Add a tag..."
                            value={tagSearch}
                            onChange={(e) => setTagSearch(e.target.value)}
                            onFocus={() => setShowDropdown(true)}
                            onBlur={() => setShowDropdown(false)}
                            className="ps-input"
                        />
                        {showDropdown && (() => {
                            const matches = availableTags.filter((tag) =>
                                tag.name.toLowerCase().includes(tagSearch.toLowerCase())
                            );
                            return (
                                <div className="browse-dropdown-list">
                                    {matches.map((tag) => (
                                        <div
                                            key={tag.id}
                                            className="browse-dropdown-item"
                                            onMouseDown={() => handleAddTag(tag.id)} // Use onMouseDown to prevent blur
                                        >
                                            {tag.name}
                                        </div>
                                    ))}
                                    {matches.length === 0 && (
                                        <div className="browse-dropdown-item se-muted">
                                            No other tags available
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                </div>
            </div>

            {/* Playlist membership (DJB-95): chips with × plus an add/search
                control; changes persist immediately */}
            <div className="se-field se-field--stacked">
                <span className="se-label">Playlists</span>
                <div className="se-chip-area">
                    {memberPlaylists.length === 0 && (
                        <span className="se-muted">Not in any playlists</span>
                    )}
                    {memberPlaylists.map((playlist) => (
                        <span key={playlist.id} className="ps-tag-chip browse-chip">
                            {playlist.name}
                            <button
                                type="button"
                                className="browse-chip-remove"
                                aria-label={`Remove from playlist ${playlist.name}`}
                                onClick={() => handleRemoveFromPlaylist(playlist)}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                    <div className="se-add-dropdown">
                        <input
                            type="text"
                            placeholder="Add to playlist..."
                            value={playlistSearch}
                            onChange={(e) => setPlaylistSearch(e.target.value)}
                            onFocus={() => setShowPlaylistDropdown(true)}
                            onBlur={() => setShowPlaylistDropdown(false)}
                            className="ps-input"
                        />
                        {showPlaylistDropdown && (() => {
                            const matches = availablePlaylists.filter((playlist) =>
                                playlist.name.toLowerCase().includes(playlistSearch.toLowerCase())
                            );
                            return (
                                <div className="browse-dropdown-list">
                                    {matches.map((playlist) => (
                                        <div
                                            key={playlist.id}
                                            className="browse-dropdown-item"
                                            onMouseDown={() => handleAddToPlaylist(playlist)} // Use onMouseDown to prevent blur
                                        >
                                            {playlist.name}
                                        </div>
                                    ))}
                                    {matches.length === 0 && (
                                        <div className="browse-dropdown-item se-muted">
                                            No other playlists available
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                </div>
            </div>

            {/* Track properties */}
            <div className="se-field se-field--stacked">
                <span className="se-label">Properties</span>
                <div className="se-props">
                    {propertyChecks.map(({ key, label, checked, onToggle }) => (
                        <label key={key} className={`se-prop ${checked ? 'is-on' : ''} ${isEditing ? '' : 'is-readonly'}`}>
                            <input
                                type="checkbox"
                                checked={checked}
                                disabled={!isEditing}
                                onChange={(e) => onToggle(e.target.checked)}
                            />
                            <span className="se-prop-box" aria-hidden="true" />
                            {label}
                        </label>
                    ))}
                </div>
            </div>

            {/* Audio player: the black Replace button sits next to the timeline */}
            <div className="audio-player">
                <div className="controls">
                    <button className="ps-btn ps-btn--primary" onClick={handlePlay} disabled={isPlaying}>
                        Play
                    </button>
                    <button className="ps-btn ps-btn--ghost" onClick={handlePause} disabled={!isPlaying}>
                        Pause
                    </button>
                    <button className="ps-btn ps-btn--ghost" onClick={handleStop}>
                        Stop
                    </button>
                </div>

                <div className="se-timeline-row">
                    <div className="timeline-container">
                        <input
                            type="range"
                            className="timeline"
                            min="0"
                            max={duration}
                            step="0.1"
                            value={currentTime}
                            onChange={handleTimelineChange}
                        />
                        <div className="time-display">
                            <span>{formatTime(currentTime)}</span>
                            <span>{formatTime(duration)}</span>
                        </div>
                    </div>
                    {!selectedSong.deleted && (
                        <button className="se-replace-btn" onClick={handleReplaceClick}>
                            Replace
                        </button>
                    )}
                </div>

                {/* Volume Control */}
                <div className="volume-control">
                    <label htmlFor="volume-slider">Volume</label>
                    <input
                        id="volume-slider"
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={volume}
                        onChange={handleVolumeChange}
                        className="volume-slider"
                    />
                </div>
            </div>

            {/* DJB-198: request count at the bottom of the details, fetched
                on demand for this track only */}
            <div className="se-field se-field--requests">
                <span className="se-label">Amount of requests</span>
                <span className="se-value">
                    {requestCountError ? (
                        <span className="se-muted">Could not load</span>
                    ) : requestCount === null ? (
                        <span className="se-muted">Loading…</span>
                    ) : (
                        requestCount
                    )}
                </span>
            </div>

            {/* Footer: Delete bottom-right (or the deleted marker) */}
            <div className="se-footer">
                {selectedSong.deleted ? (
                    <p className="deleted-text">DELETED</p>
                ) : (
                    <button className="ps-btn ps-btn--danger" onClick={handleDeleteClick}>
                        Delete
                    </button>
                )}
            </div>

            {/* Hidden file input for REPLACE functionality */}
            <input
                ref={fileInputRef}
                type="file"
                accept="audio/mp3,audio/m4a,.mp3,.m4a"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
        </div>
    );
};

export default SongEdit;
